import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  UserGroupIcon,
  TicketIcon,
  CalendarIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import { Card, Badge, Avatar, InlineSpinner, EmptyState } from '@/components/ui'
import { taskforceApi } from '@/api'
import type { TaskForce } from '@/api/taskforce'

export default function MyTaskForcesPage() {
  const navigate = useNavigate()
  const [taskforces, setTaskforces] = useState<TaskForce[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchMyTaskForces()
  }, [])

  const fetchMyTaskForces = async () => {
    setLoading(true)
    try {
      const data = await taskforceApi.getMyTaskForces()
      setTaskforces(data)
    } catch (error) {
      console.error('Error fetching my task forces:', error)
      setTaskforces([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (loading) {
    return <InlineSpinner message="Caricamento le mie task force..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Le mie Task Force</h1>
          <p className="text-gray-600">Task force di cui fai parte</p>
        </div>
        <Link
          to="/taskforce"
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Tutte le Task Force
        </Link>
      </div>

      {/* Task Forces Grid */}
      {taskforces.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {taskforces.map((tf) => (
            <Link key={tf.id} to={`/taskforce/${tf.slug}`} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <UserGroupIcon className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {tf.name}
                      </h3>
                      <Badge variant={tf.is_active ? 'success' : 'default'} size="sm">
                        {tf.is_active ? 'Attiva' : 'Completata'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-500 line-clamp-3 mb-4">
                  {tf.description}
                </p>

                {/* Leader */}
                <div className="flex items-center gap-2 mb-4">
                  <Avatar size="sm" name={tf.leader.full_name} src={tf.leader.avatar || undefined} />
                  <div>
                    <p className="text-xs text-gray-500">Leader</p>
                    <p className="text-sm font-medium text-gray-900">{tf.leader.full_name}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <UserGroupIcon className="h-4 w-4" />
                      {tf.members_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <TicketIcon className="h-4 w-4" />
                      {tf.tickets_count}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400">
                    <CalendarIcon className="h-3 w-3 inline mr-1" />
                    {formatDate(tf.created_at)}
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title="Nessuna task force"
            description="Non fai ancora parte di nessuna task force. Esplora le task force disponibili e richiedi di unirti!"
            action={{
              label: 'Esplora Task Force',
              onClick: () => navigate('/taskforce')
            }}
          />
        </Card>
      )}
    </div>
  )
}
